import { Bar, BarConfig } from '@ant-design/plots';
import { useQuery } from '@apollo/client';
import { GET_USER_COUNT_BY_PURPOSE } from '@frontend/data-access';
import { getLinkLabel } from '@frontend/shared/util';
import { Card } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';

const UsersByPurpose = () => {
  const { t } = useTranslation('admin');

  const { data, loading } = useQuery<{
    userCountByPurpose: {
      count: number;
      purpose: string;
    }[];
  }>(GET_USER_COUNT_BY_PURPOSE);

  const highestPurpose = useMemo(() => {
    let highest = 0;
    let purpose = '';
    for (const item of data?.userCountByPurpose || []) {
      if (item?.count > highest) {
        highest = item.count;
        purpose = item.purpose;
      }
    }
    return purpose;
  }, [data]);

  const formattedData = useMemo(() => {
    return data?.userCountByPurpose.map((item) => ({
      ...item,
      color: item.purpose === highestPurpose ? '#47DA47' : '#529FF0',
    }));
  }, [data, highestPurpose]);

  const config: BarConfig = {
    data: formattedData || [],
    loading,
    xField: 'purpose',
    yField: 'count',
    paddingRight: 20,
    colorField: 'color',
    scale: {
      color: {
        range: ['#47DA47', '#529FF0'],
      },
    },
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
        labelFormatter(text) {
          return getLinkLabel(text);
        },
      },
    },
    tooltip: {
      title: (datum) => getLinkLabel(datum.purpose),
      formatter: (datum) => {
        return { name: 'count', value: datum.count };
      },
    },
    label: {
      text: 'count',
      position: 'right',
      textAlign: 'start',
      dx: 8,
      style: {
        fill: 'rgba(0, 0, 0, 0.65)',
        fontFamily: 'Lufga',
      },
    },
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm text-gray-900 font-medium">
              {t('users_by_onboarding_purpose')}
            </div>
            <span className="text-gray-700 font-normal">
              {t('onboarding_purpose_description')}
            </span>
          </div>
        </div>
        <Bar {...config} />
      </div>
    </Card>
  );
};

export default UsersByPurpose;
